import { useTranslation } from 'react-i18next'
import { usePricingContext } from '@/components/pricing/PricingContext'
import type { PricingDistributionInfo, ProductPricingContext } from '@/lib/pricing'

export type DistributionBenefitProgress = {
  targetAmount: number
  hasTarget: boolean
  resolvedInfo: PricingDistributionInfo | null
  requiredPurchaseAmount: number | null
  requiredPurchaseLabel: string
  progressCurrentAmount: number | null
  progressCurrentLabel: string
  progressTargetAmount: number | null
  progressTargetLabel: string
  progressPercent: number | null
  progressPercentLabel: string
  remainingDaysLabel: string
  hasRemainingDays: boolean
  canShowDetailedExplainer: boolean
}

const resolveDistributionInfo = (
  pricingContext: ProductPricingContext,
  hasTarget: boolean
) => {
  const distributionInfo = pricingContext.distributionInfo

  return (
    pricingContext.distributionEnabled
    && !pricingContext.distributionSpecial
    && pricingContext.distributionStatusResolved
    && distributionInfo?.hasData
    && hasTarget
  )
    ? distributionInfo
    : null
}

const resolveRequiredPurchaseAmount = (
  resolvedInfo: PricingDistributionInfo | null,
  targetAmount: number
) => {
  if (!resolvedInfo) return null

  const isAdaptationPeriod = resolvedInfo.mode === 'grace' && resolvedInfo.deadlineActive
  const periodPurchasedAmount = Math.max(resolvedInfo.currentMonth, resolvedInfo.average)

  return isAdaptationPeriod
    ? Math.max(0, (targetAmount * 3) - periodPurchasedAmount)
    : Math.max(0, resolvedInfo.gapToAverage * 3)
}

const resolveProgressTargetAmount = (
  resolvedInfo: PricingDistributionInfo | null,
  targetAmount: number
) => {
  if (!resolvedInfo || targetAmount <= 0) return null

  return targetAmount * 3
}

export function useDistributionBenefitProgress(): DistributionBenefitProgress {
  const pricingContext = usePricingContext()
  const { t, i18n } = useTranslation()
  const resolvedLanguage = i18n.resolvedLanguage || 'pt-BR'

  const formatCurrency = (value: number) => (
    new Intl.NumberFormat(resolvedLanguage, {
      style: 'currency',
      currency: 'BRL',
    }).format(value)
  )

  const formatPercent = (value: number) => (
    `${new Intl.NumberFormat(resolvedLanguage, {
      maximumFractionDigits: 0,
    }).format(value)}%`
  )

  const targetAmount = pricingContext.distributionInfo?.expected ?? 0
  const hasTarget = targetAmount > 0
  const resolvedInfo = resolveDistributionInfo(pricingContext, hasTarget)
  const requiredPurchaseAmount = resolveRequiredPurchaseAmount(resolvedInfo, targetAmount)
  const progressTargetAmount = resolveProgressTargetAmount(resolvedInfo, targetAmount)
  const progressCurrentAmount = progressTargetAmount !== null && requiredPurchaseAmount !== null
    ? Math.max(0, Math.min(progressTargetAmount, progressTargetAmount - requiredPurchaseAmount))
    : null
  const progressPercent = progressTargetAmount !== null && progressCurrentAmount !== null && progressTargetAmount > 0
    ? Math.max(0, Math.min(100, (progressCurrentAmount / progressTargetAmount) * 100))
    : null
  const daysRemaining = resolvedInfo?.daysRemaining ?? null
  const hasRemainingDays = Boolean(
    resolvedInfo?.deadlineActive && daysRemaining !== null
  )
  const requiredPurchaseLabel = requiredPurchaseAmount !== null
    ? formatCurrency(requiredPurchaseAmount)
    : t('product.distribution_modal_target_unavailable')
  const progressCurrentLabel = progressCurrentAmount !== null
    ? formatCurrency(progressCurrentAmount)
    : t('product.distribution_modal_target_unavailable')
  const progressTargetLabel = progressTargetAmount !== null
    ? formatCurrency(progressTargetAmount)
    : t('product.distribution_modal_target_unavailable')
  const progressPercentLabel = progressPercent !== null
    ? formatPercent(progressPercent)
    : t('product.distribution_modal_target_unavailable')
  const remainingDaysLabel = hasRemainingDays && daysRemaining !== null
    ? t('account.benefits.distribution_remaining_days', { count: daysRemaining })
    : t('account.benefits.distribution_deadline_not_applicable')
  const canShowDetailedExplainer = Boolean(
    hasTarget
    && hasRemainingDays
    && requiredPurchaseAmount !== null
    && requiredPurchaseAmount > 0
    && resolvedInfo?.mode !== 'active'
  )

  return {
    targetAmount,
    hasTarget,
    resolvedInfo,
    requiredPurchaseAmount,
    requiredPurchaseLabel,
    progressCurrentAmount,
    progressCurrentLabel,
    progressTargetAmount,
    progressTargetLabel,
    progressPercent,
    progressPercentLabel,
    remainingDaysLabel,
    hasRemainingDays,
    canShowDetailedExplainer,
  }
}
